/**
 * 智能体提及管理器
 *
 * 负责处理IM聊天中的@智能体功能：
 * 1. 检测消息中的@智能体提及
 * 2. 通过 API 静默执行智能体任务（不依赖 iframe）
 * 3. 管理活跃的智能体任务
 */

import { EventEmitter } from 'events';
import { executeAgentTask } from '@/api/agentTaskApi';

/**
 * 智能体任务状态
 */
export const TASK_STATUS = {
  PENDING: 'pending',           // 等待执行
  INITIALIZING: 'initializing', // 初始化中
  VNC_STARTING: 'vnc_starting', // VNC启动中
  AGENT_INITIALIZING: 'agent_initializing', // 智能体初始化中
  SESSION_CREATING: 'session_creating', // 会话创建中
  EXECUTING: 'executing',       // 执行中
  COMPLETING: 'completing',     // 完成中
  COMPLETED: 'completed',       // 已完成
  FAILED: 'failed',             // 失败
  CANCELLED: 'cancelled',       // 已取消
};

/**
 * 智能体提及管理器类
 */
export class AgentMentionManager extends EventEmitter {
  constructor(options = {}) {
    super();

    // 配置
    this.options = {
      // 默认超时时间（5分钟）
      defaultTimeout: 5 * 60 * 1000,
      ...options
    };

    // 活跃任务映射 Map<taskId, TaskInfo>
    this.activeTasks = new Map();

    console.log('[AgentMentionManager] 已创建（API模式）');
  }

  /**
   * 解析消息中的@智能体提及
   * @param {string} message - 消息内容
   * @param {Array} availableAgents - 可用的智能体列表 [{agentId, agentName}, ...]
   * @returns {object|null} 解析结果 { agentId, agentName, taskContent } 或 null
   */
  parseAgentMention(message, availableAgents) {
    if (!message || !availableAgents || availableAgents.length === 0) {
      console.log('[AgentMentionManager] parseAgentMention: 参数无效', {
        hasMessage: !!message,
        agentsCount: availableAgents?.length || 0
      });
      return null;
    }

    console.log('[AgentMentionManager] 开始解析消息:', message);
    console.log('[AgentMentionManager] 可用智能体:', availableAgents.map(a => ({ id: a.agentId, name: a.agentName })));

    // 优先匹配 @智能体[xxx] 格式 (IM系统标准格式)
    const bracketRegex = /@智能体\[([^\]]+)\]/g;
    let match = bracketRegex.exec(message);

    if (match) {
      const mentionedName = match[1]; // 提取方括号内的名称
      console.log('[AgentMentionManager] 匹配到 @智能体[xxx] 格式:', mentionedName);

      // 查找匹配的智能体
      const agent = availableAgents.find(a => {
        return a.agentName === mentionedName ||
               a.agentName.toLowerCase() === mentionedName.toLowerCase();
      });

      if (agent) {
        // 提取任务内容（@智能体[xxx] 之后的内容）
        const mentionEnd = match.index + match[0].length;
        const taskContent = message.substring(mentionEnd).trim();

        console.log('[AgentMentionManager] 找到匹配智能体:', {
          agentId: agent.agentId,
          agentName: agent.agentName,
          taskContent: taskContent.substring(0, 50) + '...'
        });

        return {
          agentId: agent.agentId,
          agentName: agent.agentName,
          taskContent: taskContent || message,
          originalMessage: message,
          mentionPosition: match.index
        };
      } else {
        console.log('[AgentMentionManager] 未找到匹配智能体:', mentionedName);
      }
    }

    // 回退：匹配 @智能体名称 简单模式（直接@智能体名称，无方括号）
    const simpleRegex = /@([^\s@\[\]]+)/g;

    while ((match = simpleRegex.exec(message)) !== null) {
      const mentionedName = match[1];

      // 跳过 "智能体" 本身
      if (mentionedName === '智能体') {
        continue;
      }

      // 查找匹配的智能体
      const agent = availableAgents.find(a => {
        return a.agentName === mentionedName ||
               a.agentName.toLowerCase() === mentionedName.toLowerCase();
      });

      if (agent) {
        const mentionEnd = match.index + match[0].length;
        const taskContent = message.substring(mentionEnd).trim();

        console.log('[AgentMentionManager] 简单模式找到匹配智能体:', {
          agentId: agent.agentId,
          agentName: agent.agentName
        });

        return {
          agentId: agent.agentId,
          agentName: agent.agentName,
          taskContent: taskContent || message,
          originalMessage: message,
          mentionPosition: match.index
        };
      }
    }

    console.log('[AgentMentionManager] 未找到任何@智能体提及');
    return null;
  }

  /**
   * 发送智能体任务（通过 API 静默执行）
   * @param {object} params
   * @param {string} params.agentId - 智能体ID
   * @param {string} params.agentName - 智能体名称
   * @param {string} params.taskContent - 任务内容
   * @param {object} params.imContext - IM上下文
   * @param {object} params.options - 可选配置
   * @param {Array} params.quotedMessages - 引用的IM消息列表（可选）
   * @returns {Promise<Object|null>} 任务信息 { taskId, sessionId, shareUrl } 或 null
   */
  async sendAgentTask(params) {
    const { agentId, agentName, taskContent, imContext, options = {}, quotedMessages } = params;

    try {
      // 调用 API 获取后端 taskId
      const result = await executeAgentTask({
        agentId,
        agentName,
        taskContent,
        imContext,
        quotedMessages,
        onProgress: () => {} // 进度更新通过 WebSocket 推送
      });

      if (!result.success) {
        console.error('[AgentMentionManager] API 调用失败:', result.error);
        return null;
      }

      const taskId = result.taskId || result.data?.taskId;
      if (!taskId) {
        console.error('[AgentMentionManager] 后端未返回 taskId');
        return null;
      }

      // 保存任务信息
      const taskInfo = {
        taskId,
        agentId,
        agentName,
        taskContent,
        imContext,
        quotedMessages,
        status: TASK_STATUS.RUNNING,
        progress: 0,
        message: '任务已调度',
        happySessionId: result.sessionId,
        shareUrl: result.shareUrl, // 🔥 保存 shareUrl
        startTime: Date.now(),
        options
      };

      this.activeTasks.set(taskId, taskInfo);
      this.emit('taskStarted', taskInfo);

      // 🔥 返回完整的任务信息
      return {
        taskId,
        sessionId: result.sessionId,
        shareUrl: result.shareUrl
      };
    } catch (error) {
      console.error('[AgentMentionManager] 发送任务失败:', error);
      return null;
    }
  }

  /**
   * 处理进度更新
   * @param {string} taskId - 任务ID
   * @param {object} progressData - 进度数据
   */
  handleProgressUpdate(taskId, progressData) {
    const { step, status, message } = progressData;

    let taskStatus = TASK_STATUS.EXECUTING;
    let progress = 50;

    // 映射步骤到状态
    switch (step) {
      case 'pod':
        taskStatus = TASK_STATUS.VNC_STARTING;
        progress = status === 'completed' ? 30 : 15;
        break;
      case 'task':
        taskStatus = TASK_STATUS.EXECUTING;
        progress = status === 'completed' ? 90 : 60;
        break;
      case 'error':
        taskStatus = TASK_STATUS.FAILED;
        progress = 0;
        break;
    }

    this.updateTaskStatus(taskId, taskStatus, progress, message);
  }

  /**
   * 更新任务状态
   * @param {string} taskId - 任务ID
   * @param {string} status - 状态
   * @param {number} progress - 进度
   * @param {string} message - 消息
   */
  updateTaskStatus(taskId, status, progress, message) {
    const task = this.activeTasks.get(taskId);
    if (task) {
      task.status = status;
      task.progress = progress;
      task.message = message;

      // 触发进度更新事件
      this.emit('taskProgress', task);

      console.log('[AgentMentionManager] 任务状态更新:', taskId, status, progress, message);
    }
  }

  /**
   * 取消智能体任务
   * @param {string} taskId - 任务ID
   * @returns {boolean} 是否成功取消
   */
  cancelTask(taskId) {
    const task = this.activeTasks.get(taskId);
    if (!task) {
      console.warn('[AgentMentionManager] 找不到任务:', taskId);
      return false;
    }

    // 更新任务状态
    task.status = TASK_STATUS.CANCELLED;

    // 从活跃任务中移除
    this.activeTasks.delete(taskId);

    // 触发任务取消事件
    this.emit('taskCancelled', task);

    console.log('[AgentMentionManager] 已取消任务:', taskId);

    return true;
  }

  /**
   * 获取任务信息
   * @param {string} taskId - 任务ID
   * @returns {object|null} 任务信息
   */
  getTask(taskId) {
    return this.activeTasks.get(taskId) || null;
  }

  /**
   * 获取所有活跃任务
   * @returns {Array} 活跃任务列表
   */
  getActiveTasks() {
    return Array.from(this.activeTasks.values());
  }

  /**
   * 检查是否有活跃任务
   * @returns {boolean}
   */
  hasActiveTasks() {
    return this.activeTasks.size > 0;
  }

  /**
   * 清理资源
   */
  destroy() {
    // 取消所有活跃任务
    for (const taskId of this.activeTasks.keys()) {
      this.cancelTask(taskId);
    }

    this.activeTasks.clear();
    this.removeAllListeners();

    console.log('[AgentMentionManager] 已销毁');
  }
}

// 导出单例工厂
let instance = null;

export function getAgentMentionManager(options = {}) {
  if (!instance) {
    instance = new AgentMentionManager(options);
  }
  return instance;
}

export function destroyAgentMentionManager() {
  if (instance) {
    instance.destroy();
    instance = null;
  }
}
